import { useEffect, useState, useRef } from "react";
import { Users, Plus, Pencil, Trash2, Eye, EyeOff, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAllEldoradoTeam, createEldoradoTeamMember, updateEldoradoTeamMember, deleteEldoradoTeamMember, uploadFile } from "@/lib/api";

const emptyForm = {
  name: "",
  role: "",
  description: "",
  image_url: "",
  email: "",
  phone: "",
  order: 0,
  is_active: true
};

export default function AdminEldoradoTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchMembers = async () => {
    try {
      const res = await getAllEldoradoTeam();
      setMembers(res.data);
    } catch (error) {
      toast.error("Kon teamleden niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (member) => {
    setFormData({
      name: member.name,
      role: member.role,
      description: member.description || "",
      image_url: member.image_url || "",
      email: member.email || "",
      phone: member.phone || "",
      order: member.order,
      is_active: member.is_active
    });
    setSelectedMember(member);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData({...formData, image_url: res.data.url});
      toast.success("Foto geüpload");
    } catch (error) {
      toast.error("Kon foto niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.role) {
      toast.error("Naam en rol zijn verplicht");
      return;
    }

    try {
      if (isEditing) {
        await updateEldoradoTeamMember(selectedMember.id, formData);
        toast.success("Teamlid bijgewerkt");
      } else {
        await createEldoradoTeamMember(formData);
        toast.success("Teamlid toegevoegd");
      }
      setDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error("Kon teamlid niet opslaan");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEldoradoTeamMember(selectedMember.id);
      toast.success("Teamlid verwijderd");
      setDeleteDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error("Kon teamlid niet verwijderen");
    }
  };

  const toggleActive = async (member) => {
    try {
      await updateEldoradoTeamMember(member.id, { is_active: !member.is_active });
      toast.success(member.is_active ? "Teamlid verborgen" : "Teamlid zichtbaar");
      fetchMembers();
    } catch (error) {
      toast.error("Kon status niet wijzigen");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-eldorado-team-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Sport Eldorado Team</h1>
            <p className="text-slate-600">Beheer teamleden van Sport Eldorado</p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Teamlid Toevoegen
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 bg-slate-50">
                <TableHead className="text-slate-700 font-semibold">Foto</TableHead>
                <TableHead className="text-slate-700 font-semibold">Naam</TableHead>
                <TableHead className="text-slate-700 font-semibold">Rol</TableHead>
                <TableHead className="text-slate-700 font-semibold">Email</TableHead>
                <TableHead className="text-slate-700 font-semibold">Volgorde</TableHead>
                <TableHead className="text-slate-700 font-semibold">Status</TableHead>
                <TableHead className="text-slate-700 font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length > 0 ? (
                members.map((member) => (
                  <TableRow key={member.id} className="border-slate-200 hover:bg-slate-50">
                    <TableCell>
                      {member.image_url ? (
                        <img src={member.image_url} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <Users className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{member.name}</TableCell>
                    <TableCell className="text-slate-600">{member.role}</TableCell>
                    <TableCell className="text-slate-600">{member.email || "-"}</TableCell>
                    <TableCell className="text-slate-600">{member.order}</TableCell>
                    <TableCell>
                      <Badge className={member.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                        {member.is_active ? "Actief" : "Verborgen"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleActive(member)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
                          {member.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleOpenEdit(member)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => { setSelectedMember(member); setDeleteDialogOpen(true); }}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Geen teamleden gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{isEditing ? "Teamlid Bewerken" : "Teamlid Toevoegen"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Naam *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Volledige naam"
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Rol *</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  placeholder="bijv. Coördinator"
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Beschrijving</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Korte beschrijving"
                rows={2}
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Foto</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="URL of upload een foto"
                  className="flex-1 border-slate-300"
                />
                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" ref={fileInputRef} />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-slate-300">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "..." : "Upload"}
                </Button>
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-20 h-20 rounded-full object-cover mt-2" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Telefoon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Volgorde</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  className="border-slate-300"
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
                <Label className="text-slate-700">Actief</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isEditing ? "Opslaan" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Teamlid Verwijderen</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">Weet je zeker dat je dit teamlid wilt verwijderen?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">Annuleren</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
